import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ReactNode } from 'react';
import type { MessageDescriptor } from 'react-intl';
import { FormattedMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';

import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { StreakFire } from '@sorare/core/src/atoms/icons/StreakFire';
import { iconChevronDoubleBottom } from '@sorare/core/src/atoms/icons/system/iconChevronDoubleBottom.ts';
import { iconChevronDoubleTop } from '@sorare/core/src/atoms/icons/system/iconChevronDoubleTop.ts';
import { iconGift } from '@sorare/core/src/atoms/icons/system/iconGift.ts';
import { iconTrophy } from '@sorare/core/src/atoms/icons/system/iconTrophy.ts';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandL, BrandM } from '@sorare/core/src/atoms/typography';
import { useIsMobile } from '@sorare/core/src/hooks/device/useIsMobile';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { GameweekDate } from '#src/components/so5/LastFixtureStory/GameweekDate/index.tsx';
import type { StoryState } from '#src/components/so5/LastFixtureStory/types.ts';

import type { Title_so5Fixture } from './__generated__/index.graphql.ts';

const TitleRow = styled(Horizontal).attrs({ gap: 0 })`
  padding: 0;
  gap: var(--double-unit);
  @media ${tabletAndAbove} {
    gap: 80px;
  }
`;

const titles: { [key in StoryState]?: MessageDescriptor } = defineMessages({
  promotion: {
    defaultMessage: 'You got promoted',
  },
  relegation: {
    defaultMessage: 'You can do better!',
  },
  cardLeveledUp: {
    defaultMessage: 'Leveled up!',
  },
  completedStreakTask: {
    defaultMessage: 'Streaks',
  },
  rewardsRecap: {
    defaultMessage: 'Rewards Recap',
  },
  cardReveal: {
    defaultMessage: 'Reward time',
  },
});
const greenGradient = 'linear-gradient(90deg, #22ff60 0%, #14993a 100%)';
const dateBg: { [key in StoryState]?: string } = {
  promotion: greenGradient,
  relegation: 'linear-gradient(90deg, #f23b27 0%, #9d190a 100%)',
  cardLeveledUp: greenGradient,
  completedStreakTask: 'linear-gradient(323deg, #FEF314 -3.1%, #FF4E22 52.7%)',
  rewardsRecap: greenGradient,
  cardReveal: 'var(--c-score-high)',
};
const icons: { [key in StoryState]?: ReactNode } = {
  promotion: (
    <FontAwesomeIcon
      icon={iconChevronDoubleTop}
      color="var(--c-score-high)"
      size="xl"
    />
  ),
  relegation: (
    <FontAwesomeIcon
      icon={iconChevronDoubleBottom}
      color="var(--c-red-400)"
      size="xl"
    />
  ),
  cardLeveledUp: (
    <FontAwesomeIcon
      icon={iconChevronDoubleTop}
      color="var(--c-score-high)"
      size="xl"
    />
  ),
  completedStreakTask: <StreakFire />,
  rewardsRecap: (
    <FontAwesomeIcon icon={iconGift} color="var(--c-white)" size="xl" />
  ),
  cardReveal: (
    <FontAwesomeIcon icon={iconTrophy} color="var(--c-white)" size="xl" />
  ),
};

type Props = {
  type: StoryState;
  so5Fixture?: Title_so5Fixture;
};
export const Title = ({ type, so5Fixture }: Props) => {
  const isMobile = useIsMobile();

  const TitleTypo = isMobile ? BrandM : BrandL;

  return (
    <Vertical center>
      {!!so5Fixture && (
        <GameweekDate
          startDate={so5Fixture.startDate}
          endDate={so5Fixture.endDate}
          colors={{
            bg: dateBg[type] || greenGradient,
            fg: 'var(--c-black)',
          }}
        />
      )}
      {!!titles[type] && !!icons[type] && (
        <TitleRow>
          {icons[type]}
          <TitleTypo className="text-center">
            <FormattedMessage {...titles[type]} />
          </TitleTypo>
          {icons[type]}
        </TitleRow>
      )}
    </Vertical>
  );
};

Title.fragments = {
  so5Fixture: gql`
    fragment Title_so5Fixture on So5Fixture {
      slug
      startDate
      endDate
    }
  ` as TypedDocumentNode<Title_so5Fixture>,
};
